import { useLiveRefresh } from "../utils/live";
import { useCallback, useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import {
  getAlertsConfig,
  saveAlertSettings,
  sendTestAlert,
  getRecentAlerts,
  createPairCode,
} from "../api/alerts";
import { pushSupported, isIOS, isStandalone, currentSubscription, enablePush, disablePush } from "../utils/push";
import { timeAgo } from "../utils/contact";

// Phone alerts: turn them on for this device, choose what buzzes, see what
// was sent. "Something broke" alerts land here too, so this doubles as the
// system health page.

const ICON = {
  lead: "📥",
  view: "👀",
  payment: "💰",
  balance_due: "⏰",
  request: "🛎️",
  emails: "📝",
  system: "⚠️",
  test: "🏌️",
};

export default function AdminAlerts() {
  const [config, setConfig] = useState(null);
  const [recent, setRecent] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [cfg, rows, sub] = await Promise.all([
        getAlertsConfig(),
        getRecentAlerts().catch(() => []),
        currentSubscription().catch(() => null),
      ]);
      setConfig(cfg);
      setRecent(rows);
      setSubscribed(Boolean(sub));
    } catch (e) {
      setError(e.message || "Could not load alerts.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh(load);

  async function turnOn() {
    setWorking(true);
    setError("");
    setNotice("");
    try {
      await enablePush(config.public_key);
      setSubscribed(true);
      const res = await sendTestAlert();
      setNotice(res.sent ? "You're set — a test alert is on its way." : "Turned on, but the test didn't go through. Try again in a minute.");
      load();
    } catch (e) {
      setError(e.message || "Could not turn alerts on.");
    } finally {
      setWorking(false);
    }
  }

  async function turnOff() {
    setWorking(true);
    setError("");
    try {
      await disablePush();
      setSubscribed(false);
      setNotice("Alerts are off on this device.");
      load();
    } catch (e) {
      setError(e.message || "Could not turn alerts off.");
    } finally {
      setWorking(false);
    }
  }

  async function test() {
    setWorking(true);
    setNotice("");
    try {
      const res = await sendTestAlert();
      setNotice(res.devices ? "Test alert sent." : "No devices have alerts on yet.");
      load();
    } catch (e) {
      setError(e.message || "Could not send a test.");
    } finally {
      setWorking(false);
    }
  }

  async function toggle(kind, value) {
    setConfig((c) => ({ ...c, settings: { ...c.settings, [kind]: value } }));
    try {
      const res = await saveAlertSettings({ [kind]: value });
      setConfig((c) => ({ ...c, settings: res.settings }));
    } catch (e) {
      setError(e.message || "Could not save that.");
      load();
    }
  }

  return (
    <AppShell title="Alerts">
      {error && (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}
      {notice && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green">
          {notice}
        </div>
      )}

      {!config ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <Section title="This phone">
            <DeviceSetup
              subscribed={subscribed}
              working={working}
              devices={config.devices}
              onOn={turnOn}
              onOff={turnOff}
              onTest={test}
            />
          </Section>

          <Section title="Alert me about">
            <ul className="divide-y divide-birdie-green/10">
              {config.kinds.map((k) => (
                <li key={k.key} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-birdie-charcoal">
                    <span className="mr-2">{ICON[k.key]}</span>
                    {k.label}
                  </span>
                  <Switch checked={config.settings[k.key] !== false} onChange={(v) => toggle(k.key, v)} label={k.label} />
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Recent alerts">
            {recent.length === 0 ? (
              <p className="text-sm text-birdie-charcoal/55">Nothing yet. Alerts show up here as they're sent.</p>
            ) : (
              <ul className="divide-y divide-birdie-green/10">
                {recent.map((a) => (
                  <li key={a.id} className="py-2.5 flex gap-3">
                    <span className="shrink-0 w-6 text-center">{ICON[a.kind] || "🔔"}</span>
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-sm font-medium ${
                          a.kind === "system" ? "text-red-700" : "text-birdie-charcoal"
                        }`}
                      >
                        {/* The phone alert leads with an emoji; the list shows it in the icon column. */}
                        {a.title.replace(/^\p{Extended_Pictographic}\uFE0F?\s*/u, "")}
                      </div>
                      {a.body && <div className="text-xs text-birdie-charcoal/60 break-words">{a.body}</div>}
                    </div>
                    <div className="shrink-0 text-[11px] text-birdie-charcoal/45 text-right">
                      {timeAgo(a.created_at)}
                      {!a.delivered_at && <div className="text-amber-700">retrying</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Sign in another device">
            <PairCode />
          </Section>
        </div>
      )}
    </AppShell>
  );
}

function DeviceSetup({ subscribed, working, devices, onOn, onOff, onTest }) {
  if (isIOS() && !isStandalone()) {
    return (
      <div className="text-sm text-birdie-charcoal/80 space-y-2">
        <p>iPhone alerts work from the Birdie Bus app on your home screen. One-time setup:</p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>
            Tap <strong>Share</strong> <span aria-hidden>(⬆️)</span> at the bottom of Safari, then{" "}
            <strong>Add to Home Screen</strong>.
          </li>
          <li>
            Open <strong>Birdie Bus</strong> from your home screen and sign in. If Google sign-in doesn't open, tap{" "}
            <strong>Use a code</strong> and enter a code from the bottom of this page.
          </li>
          <li>
            Go to <strong>More → Alerts</strong> and tap <strong>Turn on alerts</strong>.
          </li>
        </ol>
      </div>
    );
  }
  if (!pushSupported()) {
    return <p className="text-sm text-birdie-charcoal/70">This browser can't receive alerts. Try Chrome or Safari.</p>;
  }
  return (
    <div>
      <p className="text-sm text-birdie-charcoal/75">
        {subscribed
          ? "Alerts are on for this device."
          : "Get a buzz when a lead comes in, a customer opens a quote, or money lands."}
        {devices > 0 && (
          <span className="text-birdie-charcoal/50">
            {" "}
            ({devices} device{devices === 1 ? "" : "s"} getting alerts)
          </span>
        )}
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        {subscribed ? (
          <>
            <Button onClick={onTest} disabled={working}>
              Send a test
            </Button>
            <Button variant="secondary" onClick={onOff} disabled={working}>
              Turn off here
            </Button>
          </>
        ) : (
          <Button onClick={onOn} disabled={working}>
            {working ? "Turning on…" : "Turn on alerts"}
          </Button>
        )}
      </div>
    </div>
  );
}

function PairCode() {
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function make() {
    setBusy(true);
    setErr("");
    try {
      setCode(await createPairCode());
    } catch (e) {
      setErr(e.message || "Could not make a code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="text-sm text-birdie-charcoal/75">
      <p>
        For signing in on your home-screen app when Google sign-in won't open. On that device, tap{" "}
        <strong>Use a code</strong> on the sign-in screen.
      </p>
      {err && <p className="text-red-700 mt-2">{err}</p>}
      {code ? (
        <div className="mt-3 rounded-xl bg-birdie-cream px-4 py-3 text-center">
          <div className="font-mono text-2xl font-bold tracking-widest text-birdie-green">{code.code}</div>
          <div className="text-xs text-birdie-charcoal/55 mt-1">
            Works once, for {code.expires_in_minutes} minutes.
          </div>
        </div>
      ) : (
        <Button variant="secondary" onClick={make} disabled={busy} className="mt-3">
          {busy ? "…" : "Get a code"}
        </Button>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-birdie-green/12 rounded-2xl p-4">
      <h2 className="font-accent text-base text-birdie-charcoal mb-2">{title}</h2>
      {children}
    </section>
  );
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 min-w-0 min-h-0 shrink-0 rounded-full transition ${
        checked ? "bg-birdie-green" : "bg-birdie-charcoal/20"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}
